// Tipos base para el sistema SaaS multi-tenant

// Tipos de usuario
export const UserType = {
  SUPER_ADMIN: 'super_admin',
  COMPANY_ADMIN: 'company_admin',
  COMPANY_USER: 'company_user'
} as const;
export type UserType = typeof UserType[keyof typeof UserType];

// Estados
export const Status = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
  PENDING: 'pending'
} as const;
export type Status = typeof Status[keyof typeof Status];

// Planes de suscripción
export const PlanType = {
  FREE: 'free',
  BASIC: 'basic',
  PROFESSIONAL: 'professional',
  ENTERPRISE: 'enterprise'
} as const;
export type PlanType = typeof PlanType[keyof typeof PlanType];

// Interface para Empresa
export interface Company {
  id: string;
  name: string;
  tradeName?: string; // Nombre comercial
  rfc?: string; // RFC para México
  email: string;
  phone: string;
  address: {
    street: string;
    number: string;
    neighborhood?: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  };
  logo?: string;
  website?: string;
  plan: PlanType;
  maxUsers: number;
  currentUsers: number;
  status: Status;
  settings: {
    timezone: string;
    language: string;
    currency: string;
    fiscalRegime?: string;
  };
  subscription: {
    startDate: Date;
    endDate: Date;
    autoRenew: boolean;
    paymentMethod?: string;
  };
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

// Interface para Permisos
export interface Permission {
  id: string;
  code: string;
  name: string;
  description: string;
  module: string;
  actions: string[]; // ['create', 'read', 'update', 'delete']
}

// Interface para Rol
export interface Role {
  id: string;
  name: string;
  description: string;
  companyId?: string; // null para roles del sistema
  permissions: Permission[];
  isSystem: boolean; // true para roles predefinidos del sistema
  status: Status;
  createdAt: Date;
  updatedAt: Date;
}

// Interface para Perfil de Usuario
export interface UserProfile {
  firstName: string;
  lastName: string;
  phone?: string;
  mobile?: string;
  avatar?: string;
  birthDate?: Date;
  department?: string;
  position?: string;
  employeeId?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  };
  preferences: {
    language: string;
    timezone: string;
    notifications: {
      email: boolean;
      sms: boolean;
      push: boolean;
    };
    theme: 'light' | 'dark' | 'auto';
  };
}

// Interface para Usuario
export interface User {
  id: string;
  email: string;
  username: string;
  type: UserType;
  companyId?: string; // null para super admin
  profile: UserProfile;
  roles: Role[];
  status: Status;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  lastLogin?: Date;
  loginAttempts: number;
  lockedUntil?: Date;
  passwordChangedAt?: Date;
  resetPasswordToken?: string;
  resetPasswordExpires?: Date;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
}

// Interface para Sesión
export interface Session {
  id: string;
  userId: string;
  token: string;
  refreshToken: string;
  expiresAt: Date;
  ipAddress: string;
  userAgent: string;
  createdAt: Date;
}

// Interface para Log de Auditoría
export interface AuditLog {
  id: string;
  userId: string;
  companyId?: string;
  action: string;
  entity: string;
  entityId: string;
  oldValue?: any;
  newValue?: any;
  ipAddress: string;
  userAgent: string;
  timestamp: Date;
}

// Interface para Módulos del Sistema
export interface Module {
  id: string;
  code: string;
  name: string;
  description: string;
  icon: string;
  route: string;
  order: number;
  parent?: string;
  children?: Module[];
  permissions: Permission[];
  requiredPlan: PlanType[];
  isActive: boolean;
}

// Interface para Notificación
export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  link?: string;
  createdAt: Date;
}

// Interface para Plan de Suscripción
export interface SubscriptionPlan {
  id: string;
  name: string;
  code: PlanType;
  description: string;
  features: string[];
  price: {
    monthly: number;
    yearly: number;
    currency: string;
  };
  limits: {
    users: number;
    storage: number; // en GB
    apiCalls: number;
    customRoles: number;
    branches?: number;
  };
  modules: string[]; // IDs de módulos incluidos
  isPopular: boolean;
  isActive: boolean;
}

// Interface para Estado de Autenticación
export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  company: Company | null;
  token: string | null;
  refreshToken: string | null;
  permissions: Permission[];
  modules: Module[];
}

// Interface para Respuesta de API
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Interface para Filtros de Búsqueda
export interface SearchFilters {
  query?: string;
  status?: Status;
  companyId?: string;
  roleId?: string;
  startDate?: Date;
  endDate?: Date;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Helper Types
export type PartialCompany = Partial<Company>;
export type PartialUser = Partial<User>;
export type CreateCompanyDto = Omit<Company, 'id' | 'createdAt' | 'updatedAt'>;
export type CreateUserDto = Omit<User, 'id' | 'createdAt' | 'updatedAt'>;
export type UpdateCompanyDto = PartialCompany;
export type UpdateUserDto = PartialUser;